import type { CrapsRoll } from '@tv-casino/shared';
import { useCrapsThrow } from '@tv-casino/viewmodels/components/crapsThrow.js';
import styles from './CrapsDice.module.css';

/** Pip positions per face, in the die's own 100x100 view box. */
const PIP_LAYOUT: Record<number, [number, number][]> = {
  1: [[50, 50]],
  2: [[30, 30], [70, 70]],
  3: [[30, 30], [50, 50], [70, 70]],
  4: [[30, 30], [70, 30], [30, 70], [70, 70]],
  5: [[30, 30], [70, 30], [50, 50], [30, 70], [70, 70]],
  6: [[30, 26], [70, 26], [30, 50], [70, 50], [30, 74], [70, 74]],
};

const cx = (...parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(' ');

function Die({ face, className }: { face: number; className: string }) {
  return (
    <svg viewBox="0 0 100 100" className={className} aria-hidden="true">
      <rect x="4" y="4" width="92" height="92" rx="18" className={styles.dieFace} />
      {(PIP_LAYOUT[face] ?? []).map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="8.5" className={styles.diePip} />
      ))}
    </svg>
  );
}

export interface CrapsDiceProps {
  /** The published roll counter; a move under us is one throw. */
  rollNumber: number;
  /** The roll as the server published it, drawn once the dice settle. */
  dice: CrapsRoll | null;
  /** What to say before the first roll of a hand. */
  placeholder?: string;
  /** The stickman's call, held back until the dice settle. */
  caption?: string | null;
  size?: 'phone' | 'tv';
}

/**
 * The dome off a bubble-craps machine, shared by the TV felt and the phone:
 * two dice on a lit bed under glass, the blower firing them up on every
 * throw. Both surfaces feed it the same published roll counter, so they run
 * the same roller and neither can animate a roll the server did not make.
 *
 * The dice tumble through faces for as long as they are in the air and only
 * then show what the server rolled, so the total and the call arrive with
 * the dice rather than ahead of them. The reading is the point of the hold,
 * not the suspense: a total that appears before the pips stop moving makes
 * the animation look like it is lying.
 */
export function CrapsDice({
  rollNumber,
  dice,
  placeholder = 'Waiting on the come-out',
  caption = null,
  size = 'phone',
}: CrapsDiceProps) {
  const { faces, airborne } = useCrapsThrow(rollNumber, dice);
  const total = faces ? faces[0] + faces[1] : null;
  const settled = Boolean(faces) && !airborne;

  return (
    <div className={cx(styles.rig, size === 'tv' && styles.rigTv)}>
      <div
        className={cx(styles.dome, airborne && styles.domeLive)}
        role="img"
        aria-label={
          !faces
            ? placeholder
            : airborne
              ? 'The dice are rolling'
              : `The dice show ${faces[0]} and ${faces[1]}, ${total}`
        }
      >
        <span className={styles.bed} aria-hidden="true" />
        {faces ? (
          <>
            <Die face={faces[0]} className={cx(styles.die, airborne && styles.dieTossLeft)} />
            <Die face={faces[1]} className={cx(styles.die, airborne && styles.dieTossRight)} />
          </>
        ) : (
          <span className={styles.idle}>{placeholder}</span>
        )}
        <span className={styles.glass} aria-hidden="true" />
      </div>

      {/* Both readings ride the same gate: mid-throw the dome says nothing. */}
      <div className={styles.readout}>
        <span className={cx(styles.total, settled && styles.totalIn)}>
          {settled && total !== null ? total : ' '}
        </span>
        {caption && settled && <span className={styles.caption}>{caption}</span>}
      </div>
    </div>
  );
}
